import React, { useState, useRef, useEffect, CSSProperties } from 'react'
import { ChatWindow } from './ChatWindow'
import { usePopup } from '../contexts/PopupContext'
import { useChatbot } from '../contexts/ChatbotContext'

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

interface ChatbotButtonProps {
  position?: Position
  onClick?: () => void
  className?: string
}

const positionClasses: Record<Position, string> = {
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-28 left-4',
  'bottom-right': 'bottom-28 right-4'
}

export const ChatbotButton: React.FC<ChatbotButtonProps> = ({
  position = 'bottom-right',
  onClick,
  className = ''
}) => {
  const { isChatbotVisible } = useChatbot()
  const [isDragging, setIsDragging] = useState(false)
  const [pos, setPos] = useState<{x: number, y: number} | null>(null)
  const [hasBeenDragged, setHasBeenDragged] = useState(false)
  
  const buttonRef = useRef<HTMLDivElement>(null)
  const dragOffset = useRef({ x: 0, y: 0 })
  const dragStart = useRef({ x: 0, y: 0 })
  const clickStart = useRef({ x: 0, y: 0 })
  const { openPopup, closePopup, isChatOpen, setIsChatOpen } = usePopup()
  const prevIsChatOpen = React.useRef(isChatOpen)

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0]
    const element = buttonRef.current
    if (!element) return
    
    const rect = element.getBoundingClientRect()
    
    setIsDragging(true)
    clickStart.current = { x: touch.clientX, y: touch.clientY }
    dragStart.current = { x: rect.left, y: rect.top }
    dragOffset.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    }
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return

    const touch = e.touches[0]
    const newX = touch.clientX - dragOffset.current.x
    const newY = touch.clientY - dragOffset.current.y

    // Giới hạn trong vùng cha (khung mobile)
    const parent = buttonRef.current?.offsetParent as HTMLElement | null
    let maxX = 0, maxY = 0
    if (parent) {
      const parentRect = parent.getBoundingClientRect()
      const btnRect = buttonRef.current?.getBoundingClientRect()
      const btnW = btnRect?.width || 56
      const btnH = btnRect?.height || 56
      maxX = parentRect.width - btnW
      maxY = parentRect.height - btnH
    } else {
      // fallback: không có parent, dùng window
      maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 0)
      maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 0)
    }

    setPos({
      x: Math.min(Math.max(0, newX), maxX),
      y: Math.min(Math.max(0, newY), maxY)
    })

    if (!hasBeenDragged) {
      setHasBeenDragged(true)
    }
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (!isDragging) return

    const touch = e.changedTouches[0]
    setIsDragging(false)

    // Nếu khoảng cách di chuyển nhỏ, coi như là click
    const moveDistance = Math.sqrt(
      Math.pow(touch.clientX - clickStart.current.x, 2) +
      Math.pow(touch.clientY - clickStart.current.y, 2)
    )

    if (moveDistance < 5) {
      setIsChatOpen(true)
      onClick?.()
    }
  }

  // --- Kéo thả bằng chuột (desktop) ---
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const element = buttonRef.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    setIsDragging(true)
    clickStart.current = { x: e.clientX, y: e.clientY }
    dragStart.current = { x: rect.left, y: rect.top }
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return
    const newX = e.clientX - dragOffset.current.x
    const newY = e.clientY - dragOffset.current.y
    // Giới hạn trong vùng cha (khung mobile)
    const parent = buttonRef.current?.offsetParent as HTMLElement | null
    let maxX = 0, maxY = 0
    if (parent) {
      const parentRect = parent.getBoundingClientRect()
      const btnRect = buttonRef.current?.getBoundingClientRect()
      const btnW = btnRect?.width || 56
      const btnH = btnRect?.height || 56
      maxX = parentRect.width - btnW
      maxY = parentRect.height - btnH
    } else {
      maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 0)
      maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 0)
    }
    setPos({
      x: Math.min(Math.max(0, newX), maxX),
      y: Math.min(Math.max(0, newY), maxY)
    })
    if (!hasBeenDragged) {
      setHasBeenDragged(true)
    }
  }

  const handleMouseUp = (e: MouseEvent) => {
    if (!isDragging) return
    setIsDragging(false)
    // Nếu khoảng cách di chuyển nhỏ, coi như là click
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - clickStart.current.x, 2) +
      Math.pow(e.clientY - clickStart.current.y, 2)
    )
    if (moveDistance < 5) {
      setIsChatOpen(true)
      onClick?.()
    }
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchmove', handleTouchMove, { passive: true })
      window.addEventListener('touchend', handleTouchEnd)
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = 'none'
      document.body.style.overflow = 'hidden'
    } else {
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
      document.body.style.overflow = ''
    }

    return () => {
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
      document.body.style.overflow = ''
    }
  }, [isDragging])

  useEffect(() => {
    if (!prevIsChatOpen.current && isChatOpen) {
      openPopup()
    }
    if (prevIsChatOpen.current && !isChatOpen) {
      closePopup()
    }
    prevIsChatOpen.current = isChatOpen
  }, [isChatOpen])

  const style: CSSProperties = {
    position: 'fixed',
    ...(pos
      ? { left: `${pos.x}px`, top: `${pos.y}px` }
      : {
          bottom: 80, // nằm trên thanh bottom nav
          right: 'max(calc((100vw - 430px)/2), 24px)' // căn theo vùng app, 430px là max-width app
        }),
    transform: isDragging ? 'scale(1.1)' : 'scale(1)',
    transition: isDragging ? 'none' : 'transform 0.2s',
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: 9999,
    touchAction: 'none',
    userSelect: 'none',
    willChange: 'transform',
    pointerEvents: 'auto'
  }

  if (!isChatbotVisible) return null

  return (
    <>
      <div
        ref={buttonRef}
        style={{
          ...style,
          display: isChatOpen ? 'none' : 'block'
        }}
        className={`chatbot-float-btn ${!hasBeenDragged ? positionClasses[position] : ''} ${className}`}
        onTouchStart={handleTouchStart}
        onMouseDown={handleMouseDown}
      >
        {/* Pulse effect */}
        <div className="absolute inset-0 rounded-full bg-pink-500/20 animate-ping" />
        <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-pulse" />
        
        {/* Main button */}
        <div
          className="relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          style={{
            background: 'linear-gradient(135deg,rgb(14, 51, 217) 0%,rgb(58, 235, 179) 100%)',
          }}
        >
          <div className="animate-spin-slow">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-7 h-7 text-white"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
              />
            </svg>
          </div>
        </div>
      </div>

      {isChatOpen && (
        <>
          <div className="fixed inset-0 z-[9998] bg-black bg-opacity-50 transition-opacity duration-300" />
          <div className="fixed inset-0 z-[9999]">
            <ChatWindow 
              isOpen={isChatOpen}
              onClose={() => setIsChatOpen(false)}
            />
          </div>
        </>
      )}
    </>
  )
} 